/**
 * Moving about in a headset, and not being made ill by it.
 *
 * Comfort is the design problem here rather than a checkbox. This is a horror
 * game about being chased through corridors that all look the same; smooth
 * stick locomotion plus a hound is a reliable way to make an eleven-year-old
 * throw up, and a child who is sick once does not put the headset on again. So:
 *
 *  - **Teleport by default.** `render/xrteleport.ts` owns it and explains it at
 *    length; the short version is that hold-to-dash crosses ground with no
 *    optical flow, and that it is the only locomotion an Apple Vision Pro can
 *    express at all. Smooth stick movement is still here and is one setting
 *    away, but nobody has to find that setting before they can play.
 *  - **Snap turning by default.** Artificial rotation is the single biggest
 *    cause of sickness, far ahead of translation, because the inner ear has no
 *    reading for it at all. A snap gives the brain no continuous rotation to
 *    disagree with. Smooth turning is offered for the people who prefer it and
 *    is never the default.
 *  - **A tunnel that tightens with movement.** See `xrvignette.ts` for the why.
 *    It is driven from `PlayerState.speed`, which is the distance the *body*
 *    actually covered under stick control — so walking around your own room,
 *    which is real motion and upsets nobody, does not close it, and neither
 *    does a teleport, which has nothing to disagree with in the first place.
 *  - **Locomotion relative to the head, not the hand.** Both are defensible;
 *    this game has one reason to pick the head. `PlayerState.yaw` is a single
 *    number and everything downstream reads it — the interact cone, the audio
 *    listener, entity line-of-sight, the light model. Hand-directed steering
 *    would need a body facing that is not the look direction, which nothing in
 *    this codebase can represent, and inventing one would mean touching every
 *    system that reads yaw. Head-relative also matches the flat game exactly:
 *    W has always meant "the way I am looking".
 *
 * Physical movement is separate and is not vignetted, throttled or turned: the
 * player's own head is the authority on where their head is. `settleBody` is
 * what keeps the simulated body underneath it — and refuses when that would put
 * the body inside a wall, which is the moment the screen goes black.
 */

import type * as THREE from 'three';
import { TUNING } from '../core/config';
import { clamp, clamp01 } from '../core/math';
import type { GeneratedLevel, PlayerState, XRComfort } from '../core/types';
import type { XRIntentSet } from './xrintent';
import { handOf, HOLD_ARM_TIME, standable, XRTeleport, type XRHop, type XRMoveStyle } from './xrteleport';

/** Stick deflection at which a snap fires, and the one it must fall back under. */
const SNAP_TRIGGER = 0.65;
const SNAP_RELEASE = 0.35;

/** Seconds a held stick waits before snapping again. About three a second. */
const SNAP_REPEAT = 0.32;

/** How dark a snap goes, and for how long. A blink, not a fade. */
const SNAP_BLINK = 0.55;
const SNAP_BLINK_TIME = 0.06;

/** Body speed at which the tunnel is fully closed, m/s. A sprint. */
const VIGNETTE_FULL_SPEED = TUNING.player.sprintSpeed;

/** Below this the player counts as standing still and the tunnel opens. */
const VIGNETTE_IDLE_SPEED = 0.25;

/** Smooth turn rate at which the tunnel is fully closed, rad/s. */
const VIGNETTE_FULL_TURN = 2.2;

/** Seconds of silence from `move` after which its fade is released. See `sinceMove`. */
const MOVE_WATCHDOG = 0.25;

/**
 * Metres of head movement in one frame past which the reading is not believed.
 *
 * Tracking recovers from an occlusion with a jump, and a jump handed to the
 * body as a physical step would teleport the player through a wall. At 90 Hz a
 * genuine step is under 4 cm.
 */
const MAX_PHYSICAL_STEP = 0.6;

/**
 * Metres the head may stray from the body before the rig is simply recentred.
 *
 * Only reachable by walking a long way inside a large play space while the body
 * is stuck against something. Past this the offset is no longer a lean, it is a
 * different room.
 */
const MAX_LEASH = 2.5;

/** Where the comfort settings live. Their own key: they are not game settings. */
const COMFORT_KEY = 'backrooms.xr.v1';

/**
 * `XRComfort` lives in `core/types.ts`: the in-world settings screen edits it
 * and this file obeys it, and the two may not import each other. Re-exported
 * here because this is the file that owns its behaviour and its storage.
 */
export type { XRComfort } from '../core/types';
export type { XRMoveStyle } from './xrteleport';

/**
 * The comfort settings as this file stores them.
 *
 * Kept as a name of its own because `move` was carried here first, before
 * `XRComfort` had a field for it. It does now — the in-world screen writes
 * `Partial<XRComfort>` and could not otherwise have reached the setting — so
 * this is an alias, and the two are the same object in every sense.
 */
export type XRComfortSettings = XRComfort;

/** Defaults chosen for the most sensitive plausible player, not the toughest. */
export function defaultComfort(): XRComfortSettings {
  return {
    turn: 'snap',
    snapDegrees: 30,
    smoothDegrees: 110,
    vignette: 1,
    torchHand: 'right',
    moveHand: 'left',
    move: 'dash',
  };
}

/**
 * The movement style a comfort object is asking for.
 *
 * Written as a validated read rather than a field access because the object may
 * have come out of `localStorage`, where it is whatever an older build of this
 * game wrote — including nothing at all. Anything unrecognised falls back to the
 * default, which is the one both headsets can express.
 */
function readMove(comfort: Partial<XRComfortSettings>): XRMoveStyle {
  const value = comfort.move;
  return value === 'arc' || value === 'smooth' || value === 'dash' ? value : 'dash';
}

/**
 * Read the comfort settings back.
 *
 * Its own storage key rather than a field on the save blob, because
 * `save/storage.ts` and the settings menu belong to other files and this must
 * not need either of them to exist. Any failure falls through to the defaults,
 * which are the safe ones.
 */
export function loadComfort(): XRComfortSettings {
  const base = defaultComfort();
  try {
    const raw = localStorage.getItem(COMFORT_KEY);
    if (!raw) return base;
    const saved = JSON.parse(raw) as Partial<XRComfortSettings>;
    if (saved.turn === 'smooth' || saved.turn === 'snap') base.turn = saved.turn;
    if (typeof saved.snapDegrees === 'number') base.snapDegrees = clamp(saved.snapDegrees, 10, 90);
    if (typeof saved.smoothDegrees === 'number') base.smoothDegrees = clamp(saved.smoothDegrees, 30, 220);
    if (typeof saved.vignette === 'number') base.vignette = clamp01(saved.vignette);
    if (saved.torchHand === 'left' || saved.torchHand === 'right') base.torchHand = saved.torchHand;
    if (saved.moveHand === 'left' || saved.moveHand === 'right') base.moveHand = saved.moveHand;
    base.move = readMove(saved);
  } catch {
    /* A browser with storage switched off still gets to play. */
  }
  return base;
}

/** Write them back. Failure is silent and costs the player nothing. */
export function saveComfort(comfort: XRComfort): void {
  try {
    localStorage.setItem(COMFORT_KEY, JSON.stringify(comfort));
  } catch {
    /* ignore */
  }
}

/** What one frame of locomotion decided. */
export interface TurnResult {
  /** Radians to add to the rig's Y rotation. Positive turns the view left. */
  delta: number;
  /** True on the frame a snap fired, so the tunnel can blink. */
  snapped: boolean;
}

/**
 * What one frame of teleport decided, once it has been applied.
 *
 * `PlayerState` has already been written by the time this is returned. What the
 * caller still has to do with it is move the **rig** by the same delta, because
 * the head has to travel with the body or `settleBody` will immediately drag the
 * body back underneath a head that never left.
 */
export interface MoveResult {
  /** True on the frame a hop committed. */
  moved: boolean;
  /** Metres the rig must be translated by, to match the body. */
  dx: number;
  dz: number;
  /**
   * Ground the hop covered, metres.
   *
   * Add it to what `XRPresenter.afterPlayer` returns. `game.ts` already divides
   * that by `dt` and hands it to `PlayerController.externalMotion`, which is
   * what stops a crouched player counting as `hidden` while they are moving.
   */
  distance: number;
  /**
   * True while this style owns movement, so the caller must not also push the
   * stick into `InputSink.axes` — that would move the body twice.
   */
  consumesStick: boolean;
}

/**
 * The turn policy, the teleport, the stickless turn, and the tunnel's target
 * aperture.
 *
 * Holds a frame of state, so one instance lives for the length of a session.
 */
export class XRLocomotion {
  private comfort: XRComfort = defaultComfort();
  private style: XRMoveStyle = 'dash';
  private armed = true;
  private sinceSnap = 0;
  private blink = 0;
  /** Last frame's smooth turn rate, for the tunnel. */
  private turnRate = 0;
  /** The teleport's own blackout this frame; see `blackout`. */
  private hopFade = 0;
  /**
   * Seconds since `move` last spoke.
   *
   * A watchdog, and the failure it guards against is the worst one this file
   * has: `hopFade` is a level rather than an event, so a caller that stopped
   * calling `move` on the frame a hop was fading would leave the picture dark
   * for the rest of the session with no way back. A quarter of a second of
   * silence releases it.
   */
  private sinceMove = 0;
  private readonly teleport = new XRTeleport();
  private readonly result: MoveResult = { moved: false, dx: 0, dz: 0, distance: 0, consumesStick: true };

  setComfort(comfort: XRComfort): void {
    this.comfort = comfort;
    this.style = readMove(comfort);
  }

  getComfort(): Readonly<XRComfort> {
    return this.comfort;
  }

  /** How the player is moving. `'smooth'` is the only one that uses the stick. */
  get moveStyle(): XRMoveStyle {
    return this.style;
  }

  /**
   * True while movement is teleported rather than driven from the stick.
   *
   * Readable at any point in the frame, so the caller can decide whether to feed
   * `InputSink.axes` before it knows what this frame's hop did.
   */
  get teleporting(): boolean {
    return this.style !== 'smooth';
  }

  /**
   * The arc teleport's aiming curve and landing ring. Add it to the **scene**,
   * not to the rig — it marks a place in the building. Built on first call and
   * never on a machine that has not entered a session.
   */
  aimView(): THREE.Object3D {
    return this.teleport.aimView();
  }

  /** Forget everything a session left behind. Called when one starts. */
  reset(): void {
    this.armed = true;
    this.sinceSnap = 0;
    this.blink = 0;
    this.turnRate = 0;
    this.hopFade = 0;
    this.sinceMove = 0;
    this.teleport.reset();
  }

  /** Release the arc's geometry. A session ending does not need this; a teardown does. */
  dispose(): void {
    this.teleport.dispose();
  }

  /**
   * Turn the raw stick request into rig rotation.
   *
   * Snap is a state machine rather than a threshold test so that holding the
   * stick over repeats at a fixed rate instead of firing once, and letting go
   * and pushing again is always immediately answered.
   */
  turn(request: number, dt: number): TurnResult {
    const c = this.comfort;
    this.sinceSnap += dt;

    if (c.turn === 'smooth') {
      this.armed = true;
      const rate = -request * c.smoothDegrees * (Math.PI / 180);
      this.turnRate = rate;
      return { delta: rate * dt, snapped: false };
    }

    this.turnRate = 0;
    const m = Math.abs(request);
    if (m < SNAP_RELEASE) this.armed = true;
    if (m < SNAP_TRIGGER) return { delta: 0, snapped: false };
    if (!this.armed && this.sinceSnap < SNAP_REPEAT) return { delta: 0, snapped: false };

    this.armed = false;
    this.sinceSnap = 0;
    this.blink = SNAP_BLINK_TIME;
    // Positive `request` is a turn to the right, and increasing the rig's Y
    // rotation turns the view left, so the sign flips here. See `core/math`'s
    // `renderYaw` for the same conversion on the flat path.
    return { delta: -Math.sign(request) * c.snapDegrees * (Math.PI / 180), snapped: true };
  }

  /**
   * Travel: one frame of teleport, applied.
   *
   * Writes `PlayerState.pos` on the frame a hop commits, and — in a teleport
   * style only — `PlayerState.noise` every frame. Both are deliberate and both
   * have precedent in this file: `settleBody` has always written `pos` directly,
   * for the same reason (`player/controller.ts` treats it as the authority every
   * frame and does not cache it, and the destination is always somewhere the
   * body could already have stood).
   *
   * `noise` is the one that matters for play. It is normally the controller's,
   * written from the distance its own collision pass covered — which under
   * teleport is zero, so left alone a dashing player would be *silent*, the
   * hound would never find anybody, and the best counterplay in the game would
   * stop meaning anything. `render/xrteleport.ts` computes the same number
   * `player/footsteps.ts` would have from the ground the hops actually covered,
   * and it is written here, after the controller has run and before the entity
   * system reads it. See the batch report for the tidier home this wants.
   *
   * `enabled` must be false whenever the simulation is not running — a paused
   * game, a descent, a frozen player — and whenever an in-world panel is taking
   * pinches, or clicking a menu button would also send the player across the
   * room.
   */
  move(
    intents: XRIntentSet | null,
    player: PlayerState,
    level: GeneratedLevel,
    dt: number,
    enabled: boolean
  ): MoveResult {
    const out = this.result;
    const hop: XRHop = this.teleport.update(intents, player, level, dt, this.style, this.comfort.moveHand, enabled);
    this.hopFade = hop.fade;
    this.sinceMove = 0;

    out.moved = hop.moved;
    out.dx = hop.dx;
    out.dz = hop.dz;
    out.distance = hop.distance;
    out.consumesStick = hop.consumesStick;

    if (hop.moved) {
      player.pos.x += hop.dx;
      player.pos.z += hop.dz;
    }
    if (this.style !== 'smooth' && enabled) {
      player.noise = hop.noise;
      // `hidden` is the controller's "crouched and holding still" latch, and a
      // teleport is the one kind of travel it cannot see for itself. The hop's
      // metres do now reach it — `game.ts` forwards them to
      // `PlayerController.externalMotion`, which is what gives a dashing player
      // footfalls and an audible noise — but only on the frames a hop lands, and
      // `hideDelay` is 0.6 s against a crouch-dash interval of 0.88 s. So the
      // latch would come back on between hops, and a crouched player would be
      // hidden for a third of a journey across the floor: half sight range and a
      // third of the noise, at four times crouch-walking speed. Held down for as
      // long as the ground is actually being covered instead.
      if (hop.travelling) player.hidden = false;
    }
    return out;
  }

  /**
   * How closed the comfort tunnel should be, 0..1.
   *
   * `speed` is `PlayerState.speed` — the distance the body actually covered,
   * which is stick travel only. Physically walking across the room is real
   * motion the inner ear agrees with, and closing the tunnel for it would be
   * punishing the one kind of movement that never makes anybody ill. A teleport
   * does not touch it either: there is no optical flow to tunnel away from, and
   * the hop's own fade is the whole of its comfort treatment.
   */
  aperture(speed: number, dt: number): number {
    if (this.blink > 0) this.blink = Math.max(0, this.blink - dt);
    this.sinceMove += dt;
    if (this.sinceMove > MOVE_WATCHDOG) this.hopFade = 0;
    const moving = clamp01((speed - VIGNETTE_IDLE_SPEED) / (VIGNETTE_FULL_SPEED - VIGNETTE_IDLE_SPEED));
    const turning = clamp01(Math.abs(this.turnRate) / VIGNETTE_FULL_TURN);
    return this.comfort.vignette * Math.max(moving * 0.85, turning);
  }

  /**
   * The shutter: a blink over a snap turn or a hop, on top of whatever else is
   * dark.
   *
   * The two share one channel because they are the same effect and because a
   * snap turn taken mid-dash should not stack two darkenings into one deeper
   * one. Whichever is asking for more wins.
   */
  blackout(): number {
    return Math.max(this.blink > 0 ? SNAP_BLINK : 0, this.hopFade);
  }

  /**
   * The turn a runtime with no thumbsticks can still ask for.
   *
   * visionOS hands out a transient pointer on a pinch and nothing else — no
   * stick, no hover, and a ray whose origin is at the head rather than at the
   * hand, so the pinch-*drag* this used to do could never have worked there: the
   * anchor and the sample are the same point. What is left, and what is enough,
   * is which hand pinched.
   *
   * So: hold a pinch on the hand that is not the move hand and the view snaps
   * that hand's way, repeating at the same cadence a stick does, through the
   * same `turn` below — which means it inherits snap-versus-smooth, the snap
   * angle and the blink without any of them being written twice. It has to be a
   * *hold* rather than a tap because a tap is `interact`; `HOLD_ARM_TIME` is the
   * one threshold both this and the dash are measured against, so the two can
   * never disagree about where a tap ends.
   *
   * The limitation, stated rather than hidden: one hand can only ask for one
   * direction, so a player dashing with their move hand can only turn the other
   * way. Letting go of the dash frees both hands. A device with a stick never
   * reaches this at all.
   */
  /**
   * A held pinch on the hand that is not steering, read as a turn.
   *
   * **Only on hardware that also has thumbsticks.** On a stickless headset this
   * is switched off entirely, on the instruction of the person it made ill:
   * *"We don't need to have pinch to turn on AVP, you can just look or turn
   * around to turn."*
   *
   * It was worse than merely unnecessary there. visionOS reports no handedness
   * on a transient pointer, so the pinch being *held to travel* also satisfied
   * the turn test, and the player span left the whole time they moved — jerkily,
   * because a snap turn fires on a repeat timer. An earlier attempt to rescue it
   * by turning on which side of the head the pinch started only made every
   * unknown-handed pinch turn, which is the same bug wearing a hat.
   *
   * A headset with hand tracking and no sticks has no artificial turn at all,
   * which is correct: you are wearing a display that already knows which way
   * your head is pointing, and turning it is free.
   */
  pinchTurn(intents: XRIntentSet | null): number {
    if (!intents || intents.stickless) return 0;
    let request = 0;
    for (const intent of intents.all) {
      // A controller has a stick and has already been read; only a pinch or a
      // tracked hand alongside one gets here.
      if (intent.source === 'controller' || intent.source === 'none') continue;
      if (!intent.acting || intent.heldFor < HOLD_ARM_TIME) continue;
      const hand = handOf(intent);
      // Unknown handedness cannot be told from the steering hand, so it is left
      // alone rather than guessed at. See above.
      if (hand === 'none' || hand === this.comfort.moveHand) continue;
      request += hand === 'right' ? 1 : -1;
    }
    return clamp(request, -1, 1);
  }


  /**
   * True when locomotion owns the press that is happening this frame, so the
   * caller must stop calling it `interact`.
   *
   * One button means two things on both target headsets — there is no second
   * one on a Vision Pro — and the only thing that separates them is *time*. A
   * tap is an interact: it fires on the frame the press starts, which is before
   * this can ever return true. Held past `HOLD_ARM_TIME` it becomes travel,
   * which is the same threshold `XRTeleport.resolve` and `pinchTurn` arm on, so
   * the three can never disagree about where a tap ends.
   *
   * Both hands count. The move hand's hold is a dash and the other hand's is a
   * snap turn, and neither should leave `interact` latched down for the length
   * of it. A stick device never reaches either: a controller's trigger is not
   * how a dash is steered (the stick is), so its press stays an interact for as
   * long as it is held — which is what makes a Quest player able to point at a
   * granola bar and take it without setting off across the room.
   *
   * Answered from the intents alone, with no state, because the caller needs it
   * at the top of the frame and `move()` does not run until after the player
   * controller has.
   */
  claimsPress(intents: XRIntentSet | null): boolean {
    if (!intents || this.style === 'smooth') return false;
    for (const intent of intents.all) {
      if (intent.source === 'controller' || intent.source === 'none') continue;
      if (!intent.acting || intent.heldFor < HOLD_ARM_TIME) continue;
      // A hand that reports no handedness is the move hand — see `readSticks`,
      // which makes the same call for the same reason — so `none` is travel.
      if (!intent.ray && handOf(intent) === this.comfort.moveHand) continue;
      return true;
    }
    return false;
  }
}

/**
 * Put the simulated body under the player's actual head.
 *
 * The player's own legs are not something this game gets to veto, so the head
 * goes where the head goes and the body follows it when it can. Returns how
 * blocked the attempt was, 0..1, which the caller turns into a blackout: a head
 * pushed into a wall is the one case where the view has to stop following the
 * player, and going black is both the standard answer and the honest one.
 *
 * `PlayerState.pos` is written directly. That is deliberate and safe:
 * `player/controller.ts` treats `pos` as the authority every frame and does not
 * cache it, and the destination is always somewhere the body could already have
 * stood. It is never called while the player is frozen.
 */
export function settleBody(player: PlayerState, level: GeneratedLevel, headX: number, headZ: number): number {
  const r = TUNING.player.radius;
  let dx = headX - player.pos.x;
  let dz = headZ - player.pos.z;
  const dist = Math.hypot(dx, dz);
  if (dist < 1e-5) return 0;
  // A jump this big is tracking recovering from an occlusion, not a step.
  if (dist > MAX_PHYSICAL_STEP) {
    const k = MAX_PHYSICAL_STEP / dist;
    dx *= k;
    dz *= k;
  }

  const tx = player.pos.x + dx;
  const tz = player.pos.z + dz;
  if (standable(level, tx, tz, r)) {
    player.pos.x = tx;
    player.pos.z = tz;
    return 0;
  }
  // Slide: a shoulder against a wall should still let the player lean along it.
  if (standable(level, tx, player.pos.z, r)) {
    player.pos.x = tx;
  } else if (standable(level, player.pos.x, tz, r)) {
    player.pos.z = tz;
  }
  // How far into the geometry the head has gone, as a fraction of the body's
  // own radius. Full black at a body-width in, which is a head genuinely inside
  // a wall rather than a shoulder brushing one.
  return clamp01(Math.hypot(headX - player.pos.x, headZ - player.pos.z) / (r * 2));
}

/** True when the head has drifted so far from the body that a recentre is due. */
export function needsRecentre(player: PlayerState, headX: number, headZ: number): boolean {
  const dx = headX - player.pos.x;
  const dz = headZ - player.pos.z;
  return dx * dx + dz * dz > MAX_LEASH * MAX_LEASH;
}
